/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { useNav } from '../../hooks/nav';
import { ReactComponent as BackIcon } from '../../assets/images/arrowLeft.svg';
import { ReactComponent as LoadingIcon } from '../../assets/images/loading-white.svg';
import checkIcon from '../../assets/images/check.svg';
import badIcon from '../../assets/images/badIcon.svg';
import { useStore } from 'src/onboarding/store/store';
import { useMutation } from '@tanstack/react-query';
import { FileService } from 'src/onboarding/services/file.service';
import Analytics from '@web-solutions/module-analytics';
import styles from './style.module.scss';
import { Title } from 'src/onboarding/components/typography/Title/Title';
import { Text } from 'src/onboarding/components/typography/Text/Text';
import { t } from '@web-solutions/module-localization';
import { ImageUploadResponse } from 'src/onboarding/types/api';
import ScanOverlay from 'src/onboarding/components/ScanOverlay';

const SCAN_DURATION = 4000;
const SCAN_REDIRECT = 2000;

function Page() {
  const { onNext, onPrev, currentIndex } = useNav();
  const {
    setGeneratedPhotoUrl,
    setBodyImageUrl,
    setBodyImageFile,
    bodyScanError,
    setBodyScanError,
  } = useStore();
  
  const [image, setImage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showFaceScan, setShowFaceScan] = useState(false);
  const [hideScanLine, setHideScanLine] = useState(false);
  const [showErrorPopUp, setShowErrorPopUp] = useState(false);
  const [base64Image, setBase64Image] = useState('');

  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const badPhotos = [
    { label: 'onboarding.fullBodyPhoto.obstructed', img: require('../../assets/images/bad1.webp') },
    { label: 'onboarding.fullBodyPhoto.group', img: require('../../assets/images/bad2.webp') },
    { label: 'onboarding.fullBodyPhoto.background', img: require('../../assets/images/bad3.webp') },
  ];

  const goodPhotos = [
    {
      label: 'onboarding.fullBodyPhoto.fontFacing',
      img: require('../../assets/images/good1.webp'),
    },
    { label: 'onboarding.fullBodyPhoto.fullBody', img: require('../../assets/images/good2.webp') },
    { label: 'onboarding.fullBodyPhoto.fitted', img: require('../../assets/images/good3.webp') },
  ];

  useEffect(() => {
    if (image !== '') {
      processImageQuery.mutate(image);
    }
    setGeneratedPhotoUrl(null);
    setBodyScanError(true);
  }, [image]);

  useEffect(() => {
    if (showFaceScan && isImageLoaded && !isLoading) {
      Analytics.trackEvent('body-scan', 'show');
      const timer = setTimeout(() => {
        if (bodyScanError) {
          setShowErrorPopUp(true);
          Analytics.trackEvent('body-scan-error', 'show');
        }
        setHideScanLine(true);
      }, SCAN_DURATION);

      return () => clearTimeout(timer);
    }
  }, [showFaceScan, bodyScanError, isImageLoaded]);

  useEffect(() => {
    document.body.style.overflow = showFaceScan ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [showFaceScan]);

  useEffect(() => {
    if (hideScanLine && showFaceScan && !showErrorPopUp) {
      const redirectTimer = setTimeout(() => {
        onNext();
      }, SCAN_REDIRECT);

      return () => clearTimeout(redirectTimer);
    }
  }, [hideScanLine, showFaceScan, showErrorPopUp]);

  const handleChangeImage = (element) => {
    setIsImageLoaded(false);
    let file = element.files[0];
    const reader = new FileReader();
    reader.onloadend = function () {
      setShowErrorPopUp(false);
      setShowFaceScan(false);
      setHideScanLine(false);
      setImage('');
      const base64String = reader.result as string;
      setBase64Image(base64String);

      setIsLoading(true);
      setTimeout(() => {
        setImage(file);
        setBodyImageFile(file);
        setShowFaceScan(true);
        Analytics.trackEvent('full-body-user_loads', 'photo');
      }, 0);
    };
    reader.readAsDataURL(file);

    element.value = null;
  };

  const processImageQuery = useMutation({
    mutationFn: (file: string) => {
      return FileService.createImage(file);
    },
    onSuccess: (result: ImageUploadResponse) => {
      if (result.data && result.data.url) {
        setIsLoading(false);
        setBodyImageUrl(result.data.url);
        Analytics.trackEvent(`step${currentIndex + 1}`, 'complete', {
          page: 'full-body-photo',
          answer: 'continue',
        });
        setBodyScanError(false);
        poseEstimationQuery.mutate(image);
      }
    },
    onError: (error) => {
      setBodyScanError(true);
      setIsLoading(false);
      console.log(error);
      Analytics.trackEvent('full-body-files-upload', 'error');
    },
  });

  const poseEstimationQuery = useMutation({
    mutationFn: (file: string) => {
      return FileService.poseEstimation(file);
    },
    onSuccess: (result) => {
      if (result.data.keypoints) {
        setIsLoading(false);
        setBodyScanError(false);
      }
    },
    onError: (error) => {
      setBodyScanError(true);
      setIsLoading(false);
      console.log(error);
      Analytics.trackEvent('full-body-validation', 'error');
    },
  });

  return (
    <div className={'layout ltsv-photo-example_new'}>
      <div className={'back'}>
        {currentIndex !== 0 && (
          <button onClick={onPrev}>
            <BackIcon />
          </button>
        )}
        <div
          className={'skip'}
          onClick={() => {
            setBodyImageUrl('');
            Analytics.trackEvent(`step${currentIndex + 1}`, 'complete', {
              page: 'full-body-photo',
              answer: 'skip',
            });
            onNext();
          }}
        >
          {t('onboarding.fullBodyPhoto.skip')}
        </div>
      </div>
      <Title tag='h2' className={styles.title}>
        {t('onboarding.fullBodyPhoto.title')}
      </Title>

      <div className={'content'}>
        <div className='content-wrapper'>
          <div>
            <Text bold size='m' className='s-title'>
              {t('onboarding.fullBodyPhoto.goodPhoto')}
            </Text>

            <Text size='m' medium className='desc'>
              {t('onboarding.fullBodyPhoto.goodDescription')}
            </Text>
          </div>

          <div className='items'>
            {goodPhotos.map((p, index) => (
              <div className={styles.photoCard} key={index}>
                <img className={styles.photo} src={p.img} alt='good-photo' />
                <div className={styles.label}>{t(p.label)}</div>
                <img src={checkIcon} alt='check' className={styles.statusIcon} />
                <div className={styles.gradient}></div>
              </div>
            ))}
          </div>
        </div>

        <div className='content-wrapper'>
          <div>
            <Text bold size='m' className='s-title'>
              {t('onboarding.fullBodyPhoto.badPhoto')}
            </Text>

            <Text size='m' medium className='desc'>
              {t('onboarding.fullBodyPhoto.badDescription')}
            </Text>
          </div>

          <div className='items'>
            {badPhotos.map((p, index) => (
              <div className={styles.photoCard} key={index}>
                <img className={styles.photo} src={p.img} alt='bad-photo' />
                <div className={styles.label}>{t(p.label)}</div>
                <img src={badIcon} alt='check' className={styles.statusIcon} />
                <div className={styles.gradient}></div>
              </div>
            ))}
          </div>
        </div>
        <div className='overlay_new'></div>
      </div>

      <div className='controls'>
        <input
          type='file'
          id={'upload'}
          onClick={() => Analytics.trackEvent('full-body-photo_continue', 'click')}
          onChange={(e) => {
            handleChangeImage(e.target);
          }}
          hidden
        />
        <label htmlFor='upload'>
          {isLoading && (
            <span style={{ marginRight: 5 }}>
              <LoadingIcon width={20} height={20} />
            </span>
          )}
          {t('onboarding.fullBodyPhoto.continue')}
        </label>
      </div>

      {showFaceScan && !isLoading && (
        <ScanOverlay
          imageUrl={base64Image}
          isBodyScan
          hideScanLine={hideScanLine}
          isImageLoaded={isImageLoaded}
          showErrorPopUp={showErrorPopUp}
          errorTitle={t('onboarding.fullBodyPhoto.errorTitle')}
          errorDescription={t('onboarding.fullBodyPhoto.errorDescription')}
          onImageLoad={() => setIsImageLoaded(true)}
          onChoosePhoto={() => {
            setShowFaceScan(false);
            setBodyScanError(false);
            setShowErrorPopUp(false);
            Analytics.trackEvent('body-scan-choose-photo', 'click');
          }}
          onSkip={() => {
            onNext();
            Analytics.trackEvent('body-scan-skip', 'click');
          }}
        />
      )}
    </div>
  );
}
export default Page;
